import React, { useState, useCallback, useRef } from 'react';
import {
  X, Bot, Database, Upload, FileText, CheckCircle2,
  ExternalLink, Play, Loader2, Image as ImageIcon, BarChart3,
  Wrench, ChevronDown, ChevronRight, Clock,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useCoworkStore, type PanelEntityType, type PanelAction } from '@/store/coworkStore';
import { knowledgeService } from '@/services/knowledge.service';
import toast from 'react-hot-toast';

// ============= Tool name → friendly label =============
function friendlyToolName(tool: string): string {
  return tool.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function entityIcon(type: PanelEntityType | null) {
  switch (type) {
    case 'agent': case 'agent_list': return <Bot className="w-4 h-4" />;
    case 'knowledge_base': case 'knowledge_base_list': return <Database className="w-4 h-4" />;
    case 'stats': return <BarChart3 className="w-4 h-4" />;
    default: return <Wrench className="w-4 h-4" />;
  }
}

function entityLabel(type: PanelEntityType | null, toolName: string | null): string {
  switch (type) {
    case 'agent': return 'Agent';
    case 'agent_list': return 'Agents';
    case 'knowledge_base': return 'Knowledge Base';
    case 'knowledge_base_list': return 'Knowledge Bases';
    case 'training': return 'Training';
    case 'stats': return 'Analytics';
    default: return toolName ? friendlyToolName(toolName) : 'Result';
  }
}

// ============= KB Panel =============
const KBPanel: React.FC<{ data: any; onAction: (msg: string) => void }> = ({ data, onAction }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<Array<{ name: string; status: string }>>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const kbId = data?.id;
  const kbName = data?.name || 'Knowledge Base';

  const handleFiles = useCallback((newFiles: File[]) => {
    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length) handleFiles(dropped);
  }, [handleFiles]);

  const handleUpload = async () => {
    if (!kbId || files.length === 0) return;
    setUploading(true);
    try {
      const result = await knowledgeService.uploadDocuments(kbId, files);
      setUploadResults(result.documents.map(d => ({ name: d.name, status: d.status })));
      const names = files.map(f => f.name).join(', ');
      setFiles([]);
      toast.success(`Uploaded ${result.total_queued} file(s)`);
      onAction(`I just uploaded ${result.total_queued} document(s) to "${kbName}": ${names}. They are now being processed.`);
    } catch (err: any) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (idx: number) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  return (
    <div className="space-y-3">
      {/* KB Info */}
      <div className="bg-violet-50 rounded-lg p-3 border border-violet-200">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-violet-600" />
          <span className="text-sm font-semibold text-violet-900">{kbName}</span>
        </div>
        {data?.description && (
          <p className="text-xs text-violet-600 mt-1">{data.description}</p>
        )}
        {data?.document_count != null && (
          <p className="text-[10px] text-violet-400 mt-1">{data.document_count} document(s)</p>
        )}
      </div>

      {/* Upload Dropzone */}
      {kbId && (
        <>
          <div
            ref={dropRef}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all',
              dragOver
                ? 'border-violet-500 bg-violet-50'
                : 'border-gray-300 hover:border-violet-400 hover:bg-gray-50',
            )}
          >
            <Upload className={cn('w-8 h-8 mx-auto mb-2', dragOver ? 'text-violet-500' : 'text-gray-300')} />
            <p className="text-sm text-gray-600 font-medium">Drop files here</p>
            <p className="text-xs text-gray-400 mt-1">PDF, DOC, TXT, CSV, XLS</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            onChange={(e) => handleFiles(Array.from(e.target.files || []))}
            className="hidden"
            accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.md,.json"
            multiple
          />

          {/* Selected Files */}
          {files.length > 0 && (
            <div className="space-y-1.5">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-2.5 py-1.5 text-xs">
                  {f.type.startsWith('image/') ? (
                    <ImageIcon className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                  ) : (
                    <FileText className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                  )}
                  <span className="truncate flex-1 text-gray-700">{f.name}</span>
                  <span className="text-gray-400 flex-shrink-0">{formatSize(f.size)}</span>
                  <button onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-500 flex-shrink-0">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button
                onClick={handleUpload}
                disabled={uploading}
                className={cn(
                  'w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg transition-all',
                  'bg-violet-500 text-white border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e]',
                  'hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                )}
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? 'Uploading...' : `Upload ${files.length} file(s)`}
              </button>
            </div>
          )}

          {/* Upload Results */}
          {uploadResults.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-500">Uploaded</p>
              {uploadResults.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  <span className="truncate">{r.name}</span>
                  <span className="text-gray-400 ml-auto">{r.status}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============= Agent Panel =============
const AgentPanel: React.FC<{ data: any }> = ({ data }) => {
  if (!data) return null;

  const statusColors: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    draft: 'bg-gray-100 text-gray-600 border-gray-200',
    paused: 'bg-amber-100 text-amber-700 border-amber-200',
  };

  return (
    <div className="space-y-3">
      <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold text-blue-900">{data.name}</span>
          {data.status && (
            <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full border font-medium ml-auto', statusColors[data.status] || statusColors.draft)}>
              {data.status}
            </span>
          )}
        </div>
        {data.description && (
          <p className="text-xs text-blue-600 mt-1.5 line-clamp-3">{data.description}</p>
        )}
      </div>

      {data.id && (
        <a
          href={`/agents/${data.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'flex items-center justify-center gap-2 w-full px-3 py-2 text-sm font-medium rounded-lg',
            'bg-white text-gray-700 border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e]',
            'hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all',
          )}
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Open Agent Config
        </a>
      )}
    </div>
  );
};

// ============= Agent List Panel =============
const AgentListPanel: React.FC<{ data: any }> = ({ data }) => {
  const agents = Array.isArray(data) ? data : [];
  if (agents.length === 0) return <p className="text-xs text-gray-400">No agents found</p>;

  return (
    <div className="space-y-2">
      {agents.slice(0, 10).map((agent: any) => (
        <a
          key={agent.id}
          href={`/agents/${agent.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2.5 p-2.5 rounded-lg border border-gray-200 hover:border-violet-300 hover:bg-violet-50 transition-colors"
        >
          <div className="w-8 h-8 rounded-lg bg-blue-100 border border-blue-200 flex items-center justify-center flex-shrink-0">
            <Bot className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{agent.name}</p>
            <p className="text-[10px] text-gray-400">{agent.status || 'draft'}</p>
          </div>
          <ExternalLink className="w-3 h-3 text-gray-300 flex-shrink-0" />
        </a>
      ))}
    </div>
  );
};

// ============= KB List Panel =============
const KBListPanel: React.FC<{ data: any; onSelectKB: (kb: any) => void }> = ({ data, onSelectKB }) => {
  const kbs = Array.isArray(data) ? data : [];
  if (kbs.length === 0) return <p className="text-xs text-gray-400">No knowledge bases found</p>;

  return (
    <div className="space-y-2">
      {kbs.map((kb: any) => (
        <button
          key={kb.id}
          onClick={() => onSelectKB(kb)}
          className="w-full flex items-center gap-2.5 p-2.5 rounded-lg border border-gray-200 hover:border-violet-300 hover:bg-violet-50 transition-colors text-left"
        >
          <div className="w-8 h-8 rounded-lg bg-violet-100 border border-violet-200 flex items-center justify-center flex-shrink-0">
            <Database className="w-4 h-4 text-violet-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{kb.name}</p>
            <p className="text-[10px] text-gray-400">{kb.document_count || 0} docs</p>
          </div>
        </button>
      ))}
    </div>
  );
};

// ============= Training Panel =============
const TrainingPanel: React.FC<{ data: any }> = ({ data }) => {
  if (!data) return null;

  return (
    <div className="space-y-3">
      <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
        <div className="flex items-center gap-2">
          <Play className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-semibold text-amber-900">{data.name || 'Training Ground'}</span>
        </div>
        {data.description && (
          <p className="text-xs text-amber-600 mt-1">{data.description}</p>
        )}
      </div>
      {data.id && (
        <a
          href={`/training/${data.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'flex items-center justify-center gap-2 w-full px-3 py-2 text-sm font-medium rounded-lg',
            'bg-white text-gray-700 border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e]',
            'hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all',
          )}
        >
          <Play className="w-3.5 h-3.5" />
          Open Training
        </a>
      )}
    </div>
  );
};

// ============= Stats Panel =============
const StatsPanel: React.FC<{ data: any }> = ({ data }) => {
  if (!data) return null;

  // Render stats as a clean key-value list
  const entries = typeof data === 'object' ? Object.entries(data) : [];

  return (
    <div className="space-y-2">
      {entries.map(([key, value]) => (
        <div key={key} className="flex items-center justify-between py-1.5 px-2 rounded bg-gray-50 text-xs">
          <span className="text-gray-500">{key.replace(/_/g, ' ')}</span>
          <span className="font-medium text-gray-800">
            {typeof value === 'number' ? value.toLocaleString() : String(value)}
          </span>
        </div>
      ))}
    </div>
  );
};

// ============= Generic Panel =============
const GenericPanel: React.FC<{ data: any; toolName: string }> = ({ data, toolName }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="space-y-3">
      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          <span className="text-sm font-medium text-gray-700">{friendlyToolName(toolName)}</span>
        </div>
      </div>
      {data && (
        <div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            View data
          </button>
          {expanded && (
            <pre className="mt-1 p-2 bg-gray-50 rounded-lg text-[10px] text-gray-600 overflow-auto max-h-48 font-mono border border-gray-200">
              {JSON.stringify(data, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

// ============= Action History =============
const ActionHistory: React.FC<{ history: PanelAction[]; onSelect: (action: PanelAction) => void }> = ({ history, onSelect }) => {
  const [expanded, setExpanded] = useState(false);
  if (history.length <= 1) return null;

  return (
    <div className="border-t border-gray-200 pt-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 mb-1"
      >
        <Clock className="w-3 h-3" />
        <span>{history.length} actions</span>
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>
      {expanded && (
        <div className="space-y-1 mt-1.5">
          {history.slice().reverse().map((action, i) => (
            <button
              key={i}
              onClick={() => onSelect(action)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left hover:bg-gray-50 transition-colors"
            >
              {entityIcon(action.entityType)}
              <span className="truncate text-gray-600">{friendlyToolName(action.toolName)}</span>
              <span className="text-[10px] text-gray-300 ml-auto flex-shrink-0">
                {action.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============= Main ActionPanel =============
export const ActionPanel: React.FC = () => {
  const { panel, closePanel, openPanel, injectPanelMessage } = useCoworkStore();

  if (!panel.isOpen) return null;

  const handleSelectHistory = (action: PanelAction) => {
    openPanel(action.entityType, action.entityData, action.toolName);
  };

  const handleSelectKB = (kb: any) => {
    openPanel('knowledge_base', kb, 'get_knowledge_base');
  };

  const renderContent = () => {
    switch (panel.entityType) {
      case 'knowledge_base':
        return <KBPanel data={panel.entityData} onAction={injectPanelMessage} />;
      case 'knowledge_base_list':
        return <KBListPanel data={panel.entityData} onSelectKB={handleSelectKB} />;
      case 'agent':
        return <AgentPanel data={panel.entityData} />;
      case 'agent_list':
        return <AgentListPanel data={panel.entityData} />;
      case 'training':
        return <TrainingPanel data={panel.entityData} />;
      case 'stats':
        return <StatsPanel data={panel.entityData} />;
      default:
        return <GenericPanel data={panel.entityData} toolName={panel.toolName || ''} />;
    }
  };

  return (
    <div className="w-[320px] flex-shrink-0 border-l-2 border-[#1a1a2e] bg-white flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-200 shrink-0">
        <div className="flex items-center gap-2 text-violet-600">
          {entityIcon(panel.entityType)}
        </div>
        <span className="text-sm font-semibold text-gray-900 flex-1">
          {entityLabel(panel.entityType, panel.toolName)}
        </span>
        <button
          onClick={closePanel}
          className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {renderContent()}
        <ActionHistory history={panel.history} onSelect={handleSelectHistory} />
      </div>
    </div>
  );
};
